import React, { useState, useRef, useEffect } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import SEOHead from '../SEOHead';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';
import { CheckCircle, Users, Zap, Shield, MessageCircle, Rocket, Target } from 'lucide-react';
import {
  captureUtmParams,
  appendUtmToAdminText,
  getUtmParams,
  getReadinessFromBands,
  formatAnswerValue,
  formatIcpAdminBlock,
  getPillarScores,
  getPillarStatus,
  computeChecklistScore,
  countAnsweredQuestions,
  isScoredBlock,
  isValidChecklistEmail,
} from '../../lib/checklistUtils';
import {
  getPilotRecommendation,
  formatPilotRecommendationReportSection,
} from '../../lib/aiPilotRecommendation';
import { Textarea } from '../ui/textarea';
import { postChecklistLead, postChecklistReportRequest } from '../../lib/checklistApi';
import DdgCaptcha from '../DdgCaptcha';
import { DDG_CAPTCHA_REQUIRED_MESSAGE } from '../../lib/ddgCaptcha';
import { buildConsentPayload } from '../../lib/consentPayload';
import PersonalDataConsent from '../PersonalDataConsent';
import MarketingConsent from '../MarketingConsent';
import { QUESTION_LABEL, WORK_EMAIL_LABEL } from '../../lib/formFieldSchemas';
import { buildChecklistResultData } from '../../lib/checklistResultMapper';
import { buildChecklistSalesEmailData } from '../../lib/checklistSalesEmailMapper';
import { buildChecklistOperationsEmailData } from '../../lib/checklistOperationsEmailMapper';
import { buildChecklistSalesEmailHeadingImages } from '../../lib/checklistSalesEmailHeadingImages';
import { buildChecklistOperationsEmailHeadingImages } from '../../lib/checklistOperationsEmailHeadingImages';
import { renderChecklistSalesEmailHtml } from '../../lib/renderChecklistSalesEmailHtml';
import { renderChecklistOperationsEmailHtml } from '../../lib/renderChecklistOperationsEmailHtml';
import ChecklistResultScreen from './ChecklistResultScreen';

const blockColors = [
  { bg: 'from-blue-500 to-blue-700' },
  { bg: 'from-emerald-500 to-emerald-700' },
  { bg: 'from-purple-500 to-purple-700' },
  { bg: 'from-orange-500 to-orange-700' },
  { bg: 'from-rose-500 to-rose-700' },
  { bg: 'from-indigo-500 to-indigo-700' },
];

const blockIcons = [Shield, Users, Zap, MessageCircle, CheckCircle, Target];

function buildAnswersPayload(checklistData, answers, scale) {
  return checklistData.blocks.flatMap((block) => [
    `\n${block.title}`,
    ...block.questions.map((question) => {
      const answer = answers[question.id];
      return `- ${question.text}: ${formatAnswerValue(answer, scale, question)}`;
    }),
  ]);
}

const ChecklistPage = ({ pageConfig }) => {
  const {
    checklistData,
    meta,
    source,
    scale,
    seo,
    hero,
    showIcpFields,
    icpFields,
    icpProfile = 'operations',
    result: resultConfig,
    reportTitle,
    readinessBands,
    readinessScaleHelp,
    pillarLabels,
  } = pageConfig;
  const pilotRecommendationEnabled = Boolean(resultConfig?.showPilotRecommendation);

  const [step, setStep] = useState(0);
  const [lead, setLead] = useState({
    name: '',
    email: '',
    company: '',
    question: '',
    role: '',
    companySize: '',
    industry: '',
    trigger: '',
  });
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const consentAcceptedRef = useRef(false);
  const [answers, setAnswers] = useState({});
  const [currentBlock, setCurrentBlock] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [consultationLoading, setConsultationLoading] = useState(false);
  const [consultationSent, setConsultationSent] = useState(false);
  const [consultationSuccess, setConsultationSuccess] = useState(null);
  const [blockError, setBlockError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [resultCaptchaToken, setResultCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const resultCaptchaRef = useRef(null);

  useEffect(() => {
    captureUtmParams();
  }, []);

  const totalQuestions = checklistData.blocks.reduce((sum, b) => sum + b.questions.length, 0);
  const answeredQuestions = countAnsweredQuestions(checklistData, answers);
  const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const currentBlockData = checklistData.blocks[currentBlock];
  const currentColors = blockColors[currentBlock % blockColors.length];
  const currentIcon = blockIcons[currentBlockData.icon ?? currentBlock % blockIcons.length];

  const computeTotalScore = () => computeChecklistScore(checklistData, answers);

  const generateReport = () => {
    const score = answers._score ?? computeTotalScore();
    const readiness = getReadinessFromBands(score, readinessBands);

    let content = `
══════════════════════════════════════════════════════════════
${reportTitle}
══════════════════════════════════════════════════════════════

Имя: ${lead.name || '-'}
Компания: ${lead.company || '-'}
Почта: ${lead.email || '-'}
`;

    content += `
БАЛЛЫ: ${score} из ${meta.maxScore}
ГОТОВНОСТЬ: ${readiness.emoji} ${readiness.label.toUpperCase()}
`;

    if (pilotRecommendationEnabled) {
      const pillarsForRec = resultConfig.showPillars
        ? getPillarScores(checklistData, answers)
        : null;
      const pilotRec = getPilotRecommendation({
        goals: answers.automation_goals,
        totalScore: score,
        pillarScores: pillarsForRec,
      });
      content += formatPilotRecommendationReportSection(pilotRec);
    }

    content += `
══════════════════════════════════════════════════════════════
ОТВЕТЫ ПО БЛОКАМ
══════════════════════════════════════════════════════════════
`;

    checklistData.blocks.forEach((block) => {
      let blockScore = 0;
      if (isScoredBlock(block)) {
        block.questions.forEach((q) => {
          blockScore += Number(answers[q.id]) || 0;
        });
      }
      const scoreSuffix = isScoredBlock(block) ? ` ${blockScore} балл.` : '';
      content += `\n【${block.title}】${scoreSuffix}\n${block.description}\n`;
      content += `─────────────────────────────────────\n`;
      block.questions.forEach((q) => {
        const val = answers[q.id];
        if (val !== undefined) {
          content += `  ${q.id}. ${q.text}\n      => ${formatAnswerValue(val, scale, q)}\n`;
        }
      });
    });

    if (resultConfig.showPillars && pillarLabels) {
      const pillars = getPillarScores(checklistData, answers);
      content += `\n══════════════════════════════════════════════════════════════\nОПОРЫ ГОТОВНОСТИ\n══════════════════════════════════════════════════════════════\n`;
      Object.entries(pillars).forEach(([key, { score: ps, max }]) => {
        const status = getPillarStatus(ps, max);
        content += `${pillarLabels[key] || key}: ${ps}/${max} - ${status.label}\n`;
      });
    }

    content += `
══════════════════════════════════════════════════════════════
ШКАЛА:
${readinessScaleHelp}

══════════════════════════════════════════════════════════════
Дата: ${new Date().toLocaleString('ru-RU')}
АНТОЛОГИЯ - AI-решения для бизнеса
https://antologia.tech
══════════════════════════════════════════════════════════════`;

    return { content, score, readiness };
  };

  const buildLeadFormText = () => {
    let text = `Имя: ${lead.name.trim()}\nПочта: ${lead.email.trim()}\nКомпания: ${lead.company.trim()}`;
    if (showIcpFields && icpFields) {
      text += `\n\n${formatIcpAdminBlock(lead, icpProfile, icpFields)}`;
    }
    return text;
  };

  const buildAdminAnswersText = (prefix) => {
    const body = `${prefix}\n\n${buildLeadFormText()}`;
    return appendUtmToAdminText(body, getUtmParams());
  };

  const postLead = async (answersTextExtra = '') => {
    return postChecklistLead({
      name: lead.name.trim(),
      contact: lead.email.trim(),
      company: lead.company.trim(),
      source,
      answersText: buildAdminAnswersText(answersTextExtra),
      ...buildConsentPayload(consent, marketingConsent),
      question: lead.question.trim() || undefined,
      ddg_captcha_token: captchaToken,
    });
  };

  const buildCompletionAnswersText = (score, readiness) => {
    const pilotRecForAdmin = pilotRecommendationEnabled
      ? getPilotRecommendation({
          goals: answers.automation_goals,
          totalScore: score,
          pillarScores: resultConfig.showPillars
            ? getPillarScores(checklistData, answers)
            : null,
        })
      : null;

    const adminLines = [
      `Баллы: ${score}`,
      `Готовность: ${readiness.label}`,
    ];
    if (pilotRecForAdmin?.startType) {
      adminLines.push('', pilotRecForAdmin.summary);
      pilotRecForAdmin.bullets.forEach((b) => adminLines.push(`- ${b}`));
    }
    adminLines.push('', 'Ответы:', ...buildAnswersPayload(checklistData, answers, scale));
    return buildAdminAnswersText(adminLines.join('\n'));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const nameVal = lead.name?.trim();
    const emailVal = lead.email?.trim();
    const companyVal = lead.company?.trim();

    if (!nameVal || !emailVal || !companyVal) {
      alert('Пожалуйста, заполните все обязательные поля');
      return;
    }
    if (!isValidChecklistEmail(emailVal)) {
      alert('Укажите корректный email');
      return;
    }
    if (showIcpFields) {
      if (!lead.role || !lead.companySize || !lead.industry || !lead.trigger) {
        alert('Укажите роль, размер компании, отрасль и триггер');
        return;
      }
    }
    if (!consent) {
      alert('Нужно согласие на обработку персональных данных');
      return;
    }
    if (!captchaToken) {
      alert(DDG_CAPTCHA_REQUIRED_MESSAGE);
      return;
    }

    setLoading(true);
    const result = await postLead('Старт чек-листа');
    setLoading(false);
    if (!result.ok) {
      const hint =
        result.status === 408
          ? 'Сервер не ответил вовремя. Попробуйте ещё раз через минуту.'
          : 'Не удалось отправить данные. Проверьте интернет и попробуйте снова.';
      alert(result.detail || hint);
      captchaRef.current?.reset();
      setCaptchaToken('');
      return;
    }
    consentAcceptedRef.current = true;
    setStep(1);
  };

  const isQuestionAnswered = (q) => {
    const a = answers[q.id];
    if (q.type === 'multi_choice') return Array.isArray(a) && a.length > 0;
    return a !== undefined;
  };

  const isCurrentBlockComplete = () => currentBlockData.questions.every(isQuestionAnswered);

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setBlockError('');
  };

  const toggleMultiChoice = (questionId, optionValue) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const next = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      return { ...prev, [questionId]: next };
    });
    setBlockError('');
  };

  const nextBlock = () => {
    if (!isCurrentBlockComplete()) {
      const needsMulti = currentBlockData.questions.some((q) => q.type === 'multi_choice');
      setBlockError(
        needsMulti
          ? 'Выберите хотя бы один сценарий, чтобы перейти дальше'
          : 'Ответьте на все вопросы блока, чтобы перейти дальше',
      );
      return;
    }
    setBlockError('');
    if (currentBlock < checklistData.blocks.length - 1) {
      setCurrentBlock(currentBlock + 1);
    } else {
      const score = computeTotalScore();
      setAnswers((prev) => ({ ...prev, _score: score }));
      setShowResult(true);
    }
  };

  const prevBlock = () => {
    if (currentBlock > 0) {
      setCurrentBlock(currentBlock - 1);
      setBlockError('');
    }
  };

  const handleReportAndConsultation = async () => {
    const score = answers._score ?? computeTotalScore();
    const readinessResult = getReadinessFromBands(score, readinessBands);

    const isSales = pageConfig.id === 'sales';
    const emailData = isSales
      ? buildChecklistSalesEmailData({
          pageConfig,
          answers,
          checklistData,
          lead,
          meta,
          readinessBands,
        })
      : buildChecklistOperationsEmailData({
          pageConfig,
          answers,
          checklistData,
          lead,
          meta,
          readinessBands,
          scale,
        });

    let headingImages = {};
    try {
      headingImages = isSales
        ? await buildChecklistSalesEmailHeadingImages(emailData)
        : await buildChecklistOperationsEmailHeadingImages(emailData);
    } catch (error) {
      console.warn('Не удалось сгенерировать PNG-заголовки для письма', error);
    }

    const reportHtml = isSales
      ? renderChecklistSalesEmailHtml(emailData, { headingImages })
      : renderChecklistOperationsEmailHtml(emailData, { headingImages });

    const result = await postChecklistReportRequest({
      name: lead.name.trim(),
      contact: lead.email.trim(),
      company: lead.company.trim(),
      source,
      score,
      readiness: readinessResult.label,
      answersText: buildCompletionAnswersText(score, readinessResult),
      reportHtml,
      ...buildConsentPayload(consentAcceptedRef.current || consent, marketingConsent),
      ddg_captcha_token: resultCaptchaToken,
    });

    if (!result.ok) {
      alert(result.detail || 'Не удалось отправить результаты. Проверьте интернет и попробуйте снова.');
      resultCaptchaRef.current?.reset();
      setResultCaptchaToken('');
      return;
    }

    setConsultationSuccess({
      title: 'Результаты отправлены на вашу почту',
      text: 'Проверьте письмо с отчётом. Чтобы записаться на консультацию, нажмите в нём кнопку «Обсудить внедрение AI».',
    });
    setConsultationSent(true);
  };

  const handleConsultation = async () => {
    if (!resultCaptchaToken) {
      alert(DDG_CAPTCHA_REQUIRED_MESSAGE);
      return;
    }
    setConsultationLoading(true);
    try {
      if (pageConfig.id === 'sales' || pageConfig.id === 'operations') {
        await handleReportAndConsultation();
        return;
      }

      const { content } = generateReport();
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `checklist_${source}_${(lead.company || 'company').replace(/\s+/g, '_')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setConsultationSent(true);
    } catch (err) {
      console.error(err);
      alert('Произошла ошибка. Попробуйте позже.');
    } finally {
      setConsultationLoading(false);
    }
  };

  const resultScreenData = showResult
    ? buildChecklistResultData({
        pageConfig,
        answers,
        checklistData,
        meta,
        readinessBands,
      })
    : null;

  const MultiChoiceOptions = ({ question }) => {
    const selected = Array.isArray(answers[question.id]) ? answers[question.id] : [];
    return (
      <div className="space-y-2" role="group">
        {question.options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggleMultiChoice(question.id, opt.value)}
              className={`w-full text-left rounded-lg border px-4 py-3 text-sm transition-all ${
                active
                  ? 'border-accent bg-accent/10 text-foreground'
                  : 'border-border bg-muted/30 hover:bg-muted/60 text-muted-foreground'
              }`}
            >
              <span className="inline-flex items-start gap-2">
                <span
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    active ? 'border-accent bg-accent text-white' : 'border-muted-foreground/40'
                  }`}
                >
                  {active ? '✓' : ''}
                </span>
                <span>{opt.label}</span>
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const ScaleButtons = ({ questionId }) => (
    <div className="flex flex-wrap gap-2">
      {scale.values.map((num) => (
        <button
          key={num}
          type="button"
          onClick={() => handleAnswer(questionId, num)}
          className={`${
            scale.type === 'likert' ? 'min-w-[5.5rem] px-4 py-3' : 'w-11 h-11'
          } rounded-lg font-medium transition-all text-sm ${
            answers[questionId] === num
              ? 'bg-accent text-white'
              : 'bg-muted hover:bg-muted/80 text-muted-foreground'
          }`}
        >
          {scale.type === 'likert' ? scale.labels[num] : num}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <SEOHead {...seo} />
      <Header />

      <main className="max-w-3xl mx-auto px-4 py-16 md:py-24">
        {step === 0 && (
          <div className="text-center">
            {hero.badge ? (
              <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Rocket className="w-4 h-4" />
                {hero.badge}
              </div>
            ) : null}

            <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
              {hero.title.includes(hero.titleHighlight) ? (
                <>
                  {hero.title.split(hero.titleHighlight)[0]}
                  <span className="bg-gradient-to-r from-accent to-purple-600 bg-clip-text text-transparent">
                    {hero.titleHighlight}
                  </span>
                  {hero.title.split(hero.titleHighlight)[1]}
                </>
              ) : (
                hero.title
              )}
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">{hero.subtitle}</p>

            <Card className="max-w-lg mx-auto text-left">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Ваше имя *</Label>
                    <Input
                      id="name"
                      className="h-12 mt-1"
                      placeholder="Анна Сергеевна"
                      value={lead.name}
                      onChange={(e) => setLead({ ...lead, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{WORK_EMAIL_LABEL} *</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className="h-12 mt-1"
                      placeholder="anna@company.ru"
                      value={lead.email}
                      onChange={(e) => setLead({ ...lead, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="company">Название компании *</Label>
                    <Input
                      id="company"
                      className="h-12 mt-1"
                      placeholder="ООО Компания"
                      value={lead.company}
                      onChange={(e) => setLead({ ...lead, company: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="question">{QUESTION_LABEL}</Label>
                    <Textarea
                      id="question"
                      className="mt-1 min-h-[88px] resize-y"
                      placeholder="Кратко опишите задачу или вопрос"
                      value={lead.question}
                      onChange={(e) => setLead({ ...lead, question: e.target.value })}
                    />
                  </div>

                  {showIcpFields && icpFields && (
                    <>
                      <div>
                        <Label>Ваша роль *</Label>
                        <select
                          className="flex h-12 w-full mt-1 rounded-md border border-input bg-background px-3 text-sm"
                          value={lead.role}
                          onChange={(e) => setLead({ ...lead, role: e.target.value })}
                          required
                        >
                          <option value="">Выберите</option>
                          {icpFields.roles.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Размер компании *</Label>
                        <select
                          className="flex h-12 w-full mt-1 rounded-md border border-input bg-background px-3 text-sm"
                          value={lead.companySize}
                          onChange={(e) => setLead({ ...lead, companySize: e.target.value })}
                          required
                        >
                          <option value="">Выберите</option>
                          {icpFields.sizes.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Отрасль (пилот) *</Label>
                        <select
                          className="flex h-12 w-full mt-1 rounded-md border border-input bg-background px-3 text-sm"
                          value={lead.industry}
                          onChange={(e) => setLead({ ...lead, industry: e.target.value })}
                          required
                        >
                          <option value="">Выберите</option>
                          {icpFields.industries.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Триггер сейчас *</Label>
                        <select
                          className="flex h-12 w-full mt-1 rounded-md border border-input bg-background px-3 text-sm"
                          value={lead.trigger}
                          onChange={(e) => setLead({ ...lead, trigger: e.target.value })}
                          required
                        >
                          <option value="">Выберите</option>
                          {icpFields.triggers.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <PersonalDataConsent
                    id="checklist-consent"
                    className="pt-2"
                    checked={consent}
                    onCheckedChange={(c) => setConsent(Boolean(c))}
                  />

                  <MarketingConsent
                    id="checklist-marketing-consent"
                    className="pt-2"
                    checked={marketingConsent}
                    onCheckedChange={(c) => setMarketingConsent(Boolean(c))}
                  />

                  <DdgCaptcha ref={captchaRef} onToken={setCaptchaToken} />

                  <Button type="submit" size="lg" className="w-full h-12" disabled={loading || !captchaToken}>
                    {loading ? 'Отправка...' : 'Пройти чек-лист →'}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <p className="text-sm text-muted-foreground mt-6">
              После прохождения отчёт придёт на вашу почту
            </p>
          </div>
        )}

        {step === 1 && !showResult && (
          <div>
            <div className="mb-8">
              <div className="flex justify-between text-sm text-muted-foreground mb-2">
                <span>
                  Блок {currentBlock + 1} из {checklistData.blocks.length}
                </span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className={`bg-gradient-to-r ${currentColors.bg} rounded-2xl p-6 mb-6 text-white`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  {React.createElement(currentIcon, { className: 'w-5 h-5' })}
                </div>
                <div>
                  <p className="text-sm opacity-80">Раздел {currentBlock + 1}</p>
                  <h2 className="text-xl font-bold">{currentBlockData.title}</h2>
                </div>
              </div>
              <p className="mt-3 opacity-90">{currentBlockData.description}</p>
            </div>

            <div className="space-y-4 mb-4">
              {currentBlockData.questions.map((q) => (
                <div key={q.id} className="border border-border rounded-xl p-5 bg-card">
                  <p className="font-medium text-base mb-4">{q.text}</p>
                  {q.type === 'multi_choice' ? (
                    <MultiChoiceOptions question={q} />
                  ) : (
                    <ScaleButtons questionId={q.id} />
                  )}
                </div>
              ))}
            </div>

            {blockError && <p className="text-sm text-destructive mb-4">{blockError}</p>}

            <div className="flex items-center justify-between gap-4">
              <Button variant="outline" onClick={prevBlock} disabled={currentBlock === 0} size="lg">
                ← Назад
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentBlock + 1}/{checklistData.blocks.length}
              </span>
              <Button onClick={nextBlock} size="lg">
                {currentBlock === checklistData.blocks.length - 1 ? 'Получить результат' : 'Далее →'}
              </Button>
            </div>
          </div>
        )}

        {showResult && resultScreenData && (
          <ChecklistResultScreen
            data={resultScreenData}
            onCtaClick={handleConsultation}
            consultationLoading={consultationLoading}
            consultationSent={consultationSent}
            consultationSuccess={consultationSuccess}
            captcha={
              consultationSent ? null : (
                <DdgCaptcha ref={resultCaptchaRef} onToken={setResultCaptchaToken} />
              )
            }
            captchaReady={Boolean(resultCaptchaToken)}
          />
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ChecklistPage;
